// Import Types
import type {
    AIMetric,
    AIRemoteSegmentOutliner,
    AIRemoteSegmentPreviewItem,
    AIRemoteSegmentIntersection,
    AIRemoteSegmentStatItem,
    PredefinedSegment,
    SegmentById,
    AIRemoteSegmentsHistogram,
    DownloadFileResponse,
} from 'types';
// Import Helpers
import { generateCleanUrlParams } from 'services/Helpers';
// Import Configs
import {
    CRMRequestService,
    CRMRequestServiceDownload,
    getCancelableRequest,
    postCancelableRequest,
} from './http.config';

const AISegmentationHttpService = {
    getAISegmentsList: () => {
        return getCancelableRequest<PredefinedSegment[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/GetRemoteSegments',
        });
    },
    getAIMetrics: () => {
        return getCancelableRequest<AIMetric[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/GetMetrics',
        });
    },
    getRemoteSegmentPreview: () => {
        return postCancelableRequest<AIRemoteSegmentPreviewItem[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/GetRemoteSegmentPreview',
        });
    },
    createSegmentForAiPredefined: () => {
        return postCancelableRequest<SegmentById[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/CreateSegmentForAiPredefined',
        });
    },
    downloadRemoteSegments: () => {
        return postCancelableRequest<DownloadFileResponse>({
            requestService: CRMRequestServiceDownload,
            endpointPath: ({ isDownload }) =>
                `/AiSegmentation/DownloadRemoteSegments${generateCleanUrlParams({ isDownload })}`,
        });
    },
    getRemoteSegmentStats: () => {
        return postCancelableRequest<AIRemoteSegmentStatItem[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/GetRemoteSegmentStats',
        });
    },
    downloadRemoteSegmentStats: () => {
        return postCancelableRequest<DownloadFileResponse>({
            requestService: CRMRequestServiceDownload,
            endpointPath: ({ isDownload }) =>
                `/AiSegmentation/DownloadRemoteSegmentStats${generateCleanUrlParams({ isDownload })}`,
        });
    },
    getRemoteSegmentIntersections: () => {
        return postCancelableRequest<AIRemoteSegmentIntersection>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/GetRemoteSegmentIntersections',
        });
    },
    createSegmentForAiComparison: () => {
        return postCancelableRequest<SegmentById[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/CreateSegmentForAiComparison',
        });
    },
    downloadRemoteSegmentComparison: () => {
        return postCancelableRequest<DownloadFileResponse>({
            requestService: CRMRequestServiceDownload,
            endpointPath: ({ isDownload }) =>
                `/AiSegmentation/DownloadRemoteSegmentComparison${generateCleanUrlParams({ isDownload })}`,
        });
    },
    getRemoteSegmentsHistogram: () => {
        return postCancelableRequest<AIRemoteSegmentsHistogram>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/GetRemoteSegmentsHistogram',
        });
    },
    getRemoteSegmentOutliners: () => {
        return getCancelableRequest<AIRemoteSegmentOutliner[]>({
            requestService: CRMRequestService,
            endpointPath: ({ segment, method, field }) =>
                `/AiSegmentation/GetRemoteSegmentOutliners${generateCleanUrlParams({ segment, method, field })}`,
        });
    },
    createSegmentForAiOutliners: () => {
        return postCancelableRequest<SegmentById[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/CreateSegmentForAiOutliners',
        });
    },
    downloadRemoteSegmentOutliners: () => {
        return postCancelableRequest<DownloadFileResponse>({
            requestService: CRMRequestServiceDownload,
            endpointPath: ({ isDownload }) =>
                `/AiSegmentation/DownloadRemoteSegmentOutliners${generateCleanUrlParams({ isDownload })}`,
        });
    },
    getRemoteSegmentsChatBotAnswer: () => {
        return postCancelableRequest<string | AnyObject[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/AiSegmentation/getRemoteSegmentsChatBotAnswer',
        });
    },
};

export default AISegmentationHttpService;
